import {
  DynamicObjectInterface,
  ImageReturnType,
  RegistrationValidations,
  updateRegistrationData,
} from '@commerce-profile-webcomponents/sdk';
import {
  AccountType,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Input,
  InputTypes,
  LoaderType,
  NextgenImage,
  SkeletonFrame,
  SkeletonType,
  Theme,
  Toggle,
  ToggleStyle,
  useBreakpoint,
  useTranslation,
} from '@nextgen-web-framework/all';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type AppDispatch, Logger, type AppState } from '@nextgen-web-framework/core';
import { Field, FieldProps, Form, FormikProvider, useFormik } from 'formik';
import { useRegistrationFieldsMasterData, useRegistrationFlowConfig } from '../../utils/masterdata';
import { getRegistrationValidationData, registrationValidationSchema } from '../../utils/registrationUtils';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { PatchHandlerType } from '../RegistrationIdVerificationStep/types/propTypes';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import { APICallType } from '../Registration/types/propTypes';
import { IABOKitForm, RegistrationSelectAboKitStepTypeProp } from './types/propTypes';
import styles from './RegistrationSelectAboKitStep.module.scss';

const RegistrationSelectAboKitStep = ({ nextHandler }: RegistrationSelectAboKitStepTypeProp) => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const logger = Logger.getInstance();
  const viewPortType = useBreakpoint();
  const [tabIndex, setTabIndex] = useState(1);
  const [image, setImage] = useState<ImageReturnType>();
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const {
    currentIndex,
    userData,
    flowName: currentFlowName,
  } = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { patchVersion } = userData;
  const [inputFieldsInfo, setInputFieldsInfo] = useState<[{ key: string; value: string }]>();
  const selectedAccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const personalDetailsImage = accountTypeImagesCmsData?.[selectedAccountType]?.personalDetailsImage;
  const regFlowConfig = useRegistrationFlowConfig();
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const validationInputs = ['prePrintedKitId', 'reservedAccountNumber'];
  const initialValue = { reservedAccountNumber: '', prePrintedKitId: '' };
  const [isAPIVerified, setIsAPIVerified] = useState<boolean>();

  const validationfields = getRegistrationValidationData({
    config: registrationFieldsMasterData as RegistrationValidations[],
    fieldNames: validationInputs,
  });

  const validationSchema = registrationValidationSchema(validationfields, selectedAccountType);
  const formik = useFormik({
    initialValues: initialValue,
    onSubmit: (formData) => {
      logger.info('data', formData);
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
  });

  const { values, handleChange, handleBlur, setFieldError, validateField, setFieldTouched } = formik;

  const patchHandler = ({ payload, isNextButton, slicePayload }: PatchHandlerType) => {
    const finalPayload = {
      ...payload,
      version: patchVersion,
    };
    return nextHandler?.(finalPayload, slicePayload, !isNextButton);
  };

  const onFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = e.target;
    if (!isAPIVerified) {
      // this param prevent to erase error after api call
      validateField(fieldName);
      setFieldTouched(fieldName);
    } else if (!value || (value && values[fieldName as keyof IABOKitForm] !== value)) {
      // //after api call, error removed
      handleBlur(e);
      setFieldTouched(fieldName);
    }
  };

  const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = e.target;

    if (values[fieldName as keyof IABOKitForm] !== value) {
      handleChange(e);
      validateField(fieldName);
      setIsAPIVerified(false);
    }
  };

  const nextButtonHandler = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (tabIndex === 1) {
      const payload = {
        prePrintedKitId: values.prePrintedKitId,
        reservedAccountNumber: values.reservedAccountNumber,
      };
      const isNextButton = true;
      setIsAPIVerified(true);

      try {
        const res: DynamicObjectInterface = await patchHandler({ payload, isNextButton });
        if (res?.responseBody?.code) {
          const { subDetails } = res.responseBody;
          dispatch(updateRegistrationData({ aboKitVerified: false }));
          if (subDetails?.length > 0) {
            validationInputs.forEach((uiField) => {
              const errorObj = subDetails.find((item: DynamicObjectInterface) => item.fieldName === uiField);
              if (errorObj) {
                setFieldError(uiField, `profile.RegistrationSelectAboKitStep.${errorObj?.code}`);
              }
            });
          }
        } else if (res.registrationId) {
          dispatch(updateRegistrationData({ aboKitVerified: true }));
        }
      } catch (error) {
        logger.info('Error in patchHandler:', error);
      }
    } else {
      nextHandler?.(undefined, undefined, undefined, undefined, APICallType.SKIP);
    }
  };

  useEffect(() => {
    try {
      const currentFlowSteps = regFlowConfig?.[currentFlowName as keyof typeof regFlowConfig]?.steps ?? {};
      const currentStepName = Object.keys(currentFlowSteps)?.[currentIndex];
      const inputFieldsDetails = currentFlowSteps?.[currentStepName as keyof typeof currentFlowSteps]?.['config'];
      setInputFieldsInfo(inputFieldsDetails[selectedAccountType]);
    } catch (error) {
      logger.info(error);
    }
  }, []);

  useEffect(() => {
    if (accountTypeImagesCmsData && accountTypeImagesCmsData[AccountType.BusinessOwner]) {
      const { landingPageImage } = accountTypeImagesCmsData[AccountType.BusinessOwner];
      setImage(landingPageImage?.find((item: ImageReturnType) => item?.viewport.includes(viewPortType)));
    }
  }, [tabIndex]);

  const onToggle = (e: DynamicObjectInterface) => {
    setTabIndex(e?.index);
  };

  return (
    <DesktopLayout desktopImage={personalDetailsImage} imageClassName={styles['desktop-image']}>
      <div className={styles['select-kit-container']} data-testid="select-kit-container">
        <div className={styles['profile-header-logo']} data-testid="profile-header-image">
          <NextgenImage
            src={`/images/AmwayDesktopLogo.png`}
            isRelativeURL={true}
            loaderType={LoaderType.WEBFONTS}
            alt="headerLogo"
            objectFit="contain"
          />
        </div>
        <div className={styles['select-kit-main-section']}>
          <div className={styles['select-kit-toggle-section']}>
            <div className={styles['select-kit-heading']} data-testid="select-kit-heading">
              {t('profile.RegistrationSelectAboKit.aboKit')}
            </div>

            <div className={styles['select-kit-toggle']} data-testid="select-kit-toggle">
              <Toggle
                toggleStyle={ToggleStyle.TABS}
                tabList={[
                  t('profile.RegistrationSelectAboKit.prePaidKitYes'),
                  t('profile.RegistrationSelectAboKit.prePaidKitNo'),
                ]}
                theme={Theme.PURPLE}
                activeTab={tabIndex}
                onClick={onToggle}
                data-testid="toggle-testid"
              />
            </div>
          </div>
          {tabIndex == 1 && (
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <div className={styles['select-kit-details']} data-testid="select-kit-details">
                  {inputFieldsInfo?.some(
                    (field) => field.key === 'reservedAccountNumber' && field.value === 'true',
                  ) && (
                    <Field type="text" name="reservedAccountNumber">
                      {({ meta }: FieldProps) => (
                        <>
                          <Input
                            id="reservedAccountNumber"
                            data-testid="reservedAccountNumber"
                            type={InputTypes.TEXT}
                            name="reservedAccountNumber"
                            value={values.reservedAccountNumber}
                            onChange={onFieldChange}
                            onBlur={onFieldBlur}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error)}
                            label={t('profile.RegistrationSelectAboKit.reservedAccountNumber')}
                            autoComplete="off"
                          />
                        </>
                      )}
                    </Field>
                  )}
                  <Field type="text" name="prePrintedKitId">
                    {({ meta }: FieldProps) => (
                      <>
                        <Input
                          id="prePrintedKitId"
                          data-testid="prePrintedKitId"
                          type={InputTypes.TEXT}
                          name="prePrintedKitId"
                          value={values.prePrintedKitId}
                          onChange={onFieldChange}
                          onBlur={onFieldBlur}
                          status={inputErrorStatus(meta.touched, meta.error)}
                          message={inputErrorMessage(t, meta.touched, meta.error)}
                          label={t('profile.RegistrationSelectAboKit.prePrintedKitId')}
                          autoComplete="off"
                        />
                      </>
                    )}
                  </Field>
                </div>
              </Form>
            </FormikProvider>
          )}
          {tabIndex == 2 && (
            <div className={styles['select-kit-details']}>
              {image ? (
                <div className={styles['select-user-type-image']}>
                  <div className={styles['no-kit-image']}>
                    <NextgenImage
                      src={image.url}
                      loaderType={LoaderType.CONTENTSTACK}
                      alt={image.altText}
                      fill={true}
                    />
                  </div>

                  <span className={styles['payment-text']}>{t('profile.RegistrationSelectAboKit.paymentMessage')}</span>
                </div>
              ) : (
                <SkeletonFrame type={SkeletonType.FULL_WIDTH} heightValue={'190'} />
              )}
            </div>
          )}
        </div>

        <div className={`${styles['select-kit-next-button']} ${styles['sticky-bottom-bar']} `}>
          <Button
            data-testId="next-button-id"
            title={t('profile.common.nextButton')}
            buttonStyle={ButtonStyles.SOLID}
            size={ButtonSize.MEDIUM}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            disabled={tabIndex == 1 && (!formik.isValid || !formik.dirty)}
            onClick={nextButtonHandler}
          />
        </div>
      </div>
    </DesktopLayout>
  );
};

export default RegistrationSelectAboKitStep;
